import { AxiosResponse } from 'axios';
import { tr } from '../../i18n/i18n';
import { IStudentInvoicesResponse } from '../../interface/payment.interface';
import { IPostAttendanceResponseBody } from '../../interface/attendanceModel';
import {
  ICreateStudentRequestBody,
  ICreateStudentResponseBody,
  IGetClassroomResponse,
  IGetGradesResponse,
  IGetGuardiansReposne,
  IGetStudentByIdResponse,
  IgetStudentDetailsRequestBodyParams,
  IGetStudentsResponseBody,
  IStudentNextNumberResponseBody,
  IStudentTimetableResponse,
  IStudentEventResponsesResponse,
  StudentEventResponseFilter,
  IStudentLibraryResponse,
  StudentLibraryLoanFilter,
  IStudentSoftDeleteRequestBody,
  IStudentSoftDeleteResponseBody,
  IStudentUpdatePayload,
  IUpdateStudentDetailsResponseBodyParams,
  IBulkActivateStudentsResponse,
  ISendStudentInvitationPayload,
  ISendStudentInvitationResponse,
} from '../../interface/studentModel';
import api from '../api'; // Adjust the import path as needed
import qs from 'qs';
import type { IClassroomQueryParams } from '../../interface/classRoomModel';

// Function to create a student profile

export const createStudent = async (
  data: ICreateStudentRequestBody,
): Promise<ICreateStudentResponseBody> => {
  try {
    const response = await api.post<ICreateStudentResponseBody>('/student/profile', data);
    return response.data;
  } catch (error: any) {
    throw new Error(`${error.message}`);
  }
};

export const updateStudent = async (
  data: IStudentUpdatePayload,
  id: string,
): Promise<IUpdateStudentDetailsResponseBodyParams> => {
  try {
    const response = await api.put<IUpdateStudentDetailsResponseBodyParams>(
      `/student/profile/${id}`,
      data,
    );

    return response.data;
  } catch (error: any) {
    throw new Error(`${error.message}`);
  }
};

// Function to get student data with pagination and search
export const getStudentData = async (
  params: IgetStudentDetailsRequestBodyParams,
): Promise<IGetStudentsResponseBody> => {
  const response = await api.get<IGetStudentsResponseBody>('/student/profile', {
    params,
  });
  return response?.data;
};

export const getGrades = async (): Promise<IGetGradesResponse> => {
  const response = await api.get<IGetGradesResponse>('/classroom/grades');
  return response?.data;
};

export const getClassroom = async (params?: {
  gradeIds?: string[];
  curriculumIds?: string[];
  isActive?: boolean;
  fetchAll?: boolean;
}): Promise<IGetClassroomResponse> => {
  try {
    const queryParams: IClassroomQueryParams = {};

    if (params?.gradeIds && params.gradeIds.length > 0) {
      queryParams.gradeId = params.gradeIds;
    }

    if (params?.curriculumIds && params.curriculumIds.length > 0) {
      queryParams.curriculumId = params.curriculumIds;
    }

    if (!params?.fetchAll) {
      const isActiveValue = params?.isActive ?? true;
      queryParams.isActive = isActiveValue ? 'true' : 'false';
    } else {
      queryParams.isActive = undefined;
    }

    const response = await api.get<IGetClassroomResponse>('subject/classroom', {
      params: queryParams,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
    return response?.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      tr('g.unknown_error_occurred', 'Unknown error occurred');
    throw new Error(`Failed to fetch classroom data: ${errorMessage}`);
  }
};

export const getClassroomById = async (id?: string): Promise<IGetClassroomResponse> => {
  try {
    const response = await api.get<IGetClassroomResponse>('subject/classroom', {
      params: id ? { gradeId: id } : undefined,
    });
    return response?.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      tr('g.unknown_error_occurred', 'Unknown error occurred');
    throw new Error(`Failed to fetch classroom data: ${errorMessage}`);
  }
};

// Function to soft-delete a student by ID
export const softDeleteStudent = async (
  data: IStudentSoftDeleteRequestBody,
): Promise<IStudentSoftDeleteResponseBody> => {
  try {
    const response = await api.delete<IStudentSoftDeleteResponseBody>(
      `/student/profile/${data.id}`,
    );

    return response.data;
  } catch (error: any) {
    throw new Error(`${error.message}`);
  }
};

export const adminDeleteStudent = async (
  id: string,
  orgId?: string,
): Promise<IStudentSoftDeleteResponseBody> => {
  try {
    const response = await api.delete<IStudentSoftDeleteResponseBody>(`/classveew/students/${id}`, {
      params: orgId ? { orgId } : undefined,
    });

    return response.data;
  } catch (error: any) {
    throw new Error(`${error.message}`);
  }
};

export const getById = async (id: string): Promise<IGetStudentByIdResponse> => {
  try {
    const response = await api.get<IGetStudentByIdResponse>(`/student/profile/${id}`);
    return response?.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      tr('g.failed_to_fetch_student_data', 'Failed to fetch student data');
    throw new Error(errorMessage);
  }
};

export const getNextId = async (): Promise<IStudentNextNumberResponseBody> => {
  try {
    const response = await api.get<IStudentNextNumberResponseBody>(`/student/profile/admission`);
    return response?.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      tr('g.failed_to_fetch_next_admission_number', 'Failed to fetch next admission number');
    throw new Error(errorMessage);
  }
};

const getGuardiansForStudent = async (
  studentId: string,
): Promise<AxiosResponse<IGetGuardiansReposne>> => {
  const res: AxiosResponse<IGetGuardiansReposne> = await api.get(
    `/student/profile/${studentId}/guardians`,
  );
  return res;
};

/**
 * Activate multiple students in bulk
 * @param studentIds - Array of student IDs to activate
 * @returns Promise with activation results for each student
 */
export const bulkActivateStudents = async (
  studentIds: string[],
): Promise<IBulkActivateStudentsResponse> => {
  const response = await api.post<IBulkActivateStudentsResponse>('/student/profile/activate', {
    student_ids: studentIds,
  });
  return response.data;
};

/**
 * Send (or resend) login-credential invitation emails to the given students.
 * The same endpoint covers both send and resend: invoking it for an
 * already-invited student simply re-sends the invitation. The backend decides
 * the actual recipient for each student (student's own email / guardian / skip).
 */
const sendInvitation = async (
  payload: ISendStudentInvitationPayload,
): Promise<AxiosResponse<ISendStudentInvitationResponse>> => {
  const res: AxiosResponse<ISendStudentInvitationResponse> = await api.post(
    '/student/profile/invitation',
    payload,
  );
  return res;
};

/**
 * Every invoice raised against one student, paginated (CVA-1288).
 *
 * Student-scoped route, not `/payment/invoice/:id` — that one serves the parent
 * mobile app and returns the whole list unpaginated. The financial summary here
 * always spans ALL invoices, so the cards keep their meaning while a status
 * filter narrows the table.
 */
export const getStudentInvoices = async (
  studentId: string,
  params: { status?: string; page: number; pageSize: number },
): Promise<{ data: IStudentInvoicesResponse['data'] }> => {
  const response = await api.get<IStudentInvoicesResponse>(
    `/student/profile/${studentId}/invoice`,
    {
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.status ? { status: params.status } : {}),
      },
    },
  );
  return response.data;
};

/**
 * The student's timetable for the profile's Timetable tab (CVA-1273).
 *
 * `weekStart` narrows to that inclusive 7-day window. The grid reports its own
 * navigation upward, so this is called again whenever the viewed week changes.
 */
export const getStudentTimetable = async (
  studentId: string,
  weekStart?: string,
): Promise<{ data: IStudentTimetableResponse }> => {
  const response = await api.get<{ data: IStudentTimetableResponse }>(
    `/student/profile/${studentId}/timetable`,
    { params: weekStart ? { weekStart } : undefined },
  );
  return response.data;
};

/**
 * How the student has responded to the events they were invited to, for the
 * profile's Event Responses tab (CVA-1277).
 *
 * `filter` is omitted for "all". Note that `awaiting` is the ABSENCE of a
 * guardian response rather than a stored status, which is why it is a filter
 * value and not a consent/payment status.
 */
export const getStudentEventResponses = async (
  studentId: string,
  params: { filter?: StudentEventResponseFilter; page: number; pageSize: number },
): Promise<{ data: IStudentEventResponsesResponse }> => {
  const response = await api.get<{ data: IStudentEventResponsesResponse }>(
    `/student/profile/${studentId}/event-responses`,
    {
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.filter ? { filter: params.filter } : {}),
      },
    },
  );
  return response.data;
};

/**
 * This student's attendance for a date window (CVA-1272).
 *
 * Student-scoped route, but the SAME service behind it as the attendance
 * screen — so the records, the status filter and the five summary counts come
 * back in exactly the shape /attendance/record returns. The 60-day cap is
 * enforced server-side; a range beyond it is a 400, not a truncated list.
 */
export const getStudentAttendance = async (
  studentId: string,
  params: {
    startDate?: string;
    endDate?: string;
    statusFilter?: string | null;
    page: number;
    pageSize: number;
  },
): Promise<{ data: IPostAttendanceResponseBody['data'] }> => {
  const response = await api.get<IPostAttendanceResponseBody>(
    `/student/profile/${studentId}/attendance`,
    {
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.startDate ? { startDate: params.startDate } : {}),
        ...(params.endDate ? { endDate: params.endDate } : {}),
        ...(params.statusFilter ? { statusFilter: params.statusFilter } : {}),
      },
    },
  );
  return response.data;
};

/**
 * This student's library loans, for the profile's Library tab (CVA-1276).
 *
 * `filter` picks which of the tab's two tables is asking — `active` for books
 * still out, `returned` for the history. The summary comes back on every call
 * and always spans EVERY loan, so the clearance banner does not change meaning
 * when one of the tables pages.
 *
 * Not `/book/student-history/:id`: that one is unpaginated and does not check
 * the caller's organization.
 */
export const getStudentLibrary = async (
  studentId: string,
  params: {
    filter?: StudentLibraryLoanFilter;
    page: number;
    pageSize: number;
  },
): Promise<{ data: IStudentLibraryResponse }> => {
  const response = await api.get<{ data: IStudentLibraryResponse }>(
    `/student/profile/${studentId}/library`,
    {
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.filter ? { filter: params.filter } : {}),
      },
    },
  );
  return response.data;
};

export const studentService = {
  getGuardiansForStudent,
  bulkActivateStudents,
  sendInvitation,
  getStudentInvoices,
  getStudentTimetable,
  getStudentEventResponses,
  getStudentAttendance,
  getStudentLibrary,
};

export const studentDelete = async (id: string): Promise<IStudentSoftDeleteResponseBody> => {
  const response = await api.delete<IStudentSoftDeleteResponseBody>(`/student/profile/${id}`);
  return response.data;
};
